import { Auth } from 'aws-amplify';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  DEFAULT_SPARK_CHAT_BUTTON_ID,
  DEFAULT_SPARK_CHAT_CONTAINER_ID,
  useSetSparkChatDisabled,
} from '../../hooks/useOpenSparkChat';
import { getSparkUrl } from '../../stores/selectors/deployment.selector';
import { Charger } from '../../stores/types';
import { SparkChatTrigger } from '../../stores/types/sparkChat.interface';

interface SparkChatWidgetProps {
  charger: Charger;
  label: string;
  /** `{ selOption, selDates: [start, end] }` from the date picker. */
  dateTimeRange: { selDates: Date[] };
  /** `interaction_mode.trigger`, so Spark AI can tell entry points apart. */
  trigger: SparkChatTrigger;
  disabled?: boolean;
  /** DOM id of this instance's widget container. Must be unique per instance. */
  containerId?: string;
  /** DOM id of this instance's button. Must be unique per instance. */
  buttonId?: string;
}

declare global {
  interface Window {
    SparkWidget: {
      init: (config: {
        containerId?: string;
        buttonId?: string;
        buttonText: string;
        buttonClass: string;
        buttonHint: string;
        getParams: () => {
          chargerId: string | null;
          chargerName: string | null;
          timezone: string;
          endTime: Date;
          delta: number;
          companyId: string;
          serverUrl?: string;
          apiEndpoint?: string;
          botAvatarSrc?: string;
          userAvatarSrc?: string;
          trigger?: string;
        };
        getAuthToken?: () => Promise<string>;
      }) => void;
      destroy?: () => void;
    };
  }
}

/** Spark AI v2, through the Spark dashboard's authenticated proxy. */
const SPARK_CHAT_ENDPOINT = '/api/v2/chat';
const BUNDLE_PATH = '/static/assets/bundle/ChatApp.bundle.js';
const AVATAR_BASE = '/static/assets/img';

/**
 * The Spark widget bundle is a single external script. Load it exactly once for the whole
 * app, regardless of how many SparkChatWidget instances mount, so the bundle is evaluated
 * in a single scope (its chat popper is a singleton) and we don't fetch/execute the script
 * more than once.
 *
 * Keyed by src so a deployment-config change that repoints the Spark host doesn't get
 * silently ignored in favour of an already-loaded bundle from the old one.
 */
let loadedScriptSrc: string | null = null;
let scriptState: 'idle' | 'loading' | 'loaded' = 'idle';
const scriptWaiters: Array<() => void> = [];

const loadSparkScriptOnce = (src: string, onReady: () => void) => {
  if (scriptState === 'loaded' && loadedScriptSrc === src && window.SparkWidget) {
    onReady();
    return;
  }
  scriptWaiters.push(onReady);
  if (scriptState === 'loading') {
    return;
  }
  scriptState = 'loading';
  loadedScriptSrc = src;
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.onload = () => {
    scriptState = 'loaded';
    scriptWaiters.splice(0).forEach((callback) => callback());
  };
  script.onerror = () => {
    scriptState = 'idle';
    loadedScriptSrc = null;
    // Drop the failed batch of waiters; a later mount re-queues and retries, so we
    // don't fire stale init callbacks if the script eventually loads.
    scriptWaiters.splice(0);
  };
  document.head.appendChild(script);
};

/**
 * Mounts the Spark chat widget's Analyze button and points it at the v2 chat API.
 *
 * The chat UI itself lives in the Spark dashboard (`frontend/src/components/Chat`) and
 * ships as `ChatApp.bundle.js`; this component only supplies the button and the params.
 * Everything is served from the same Spark host that answers `/api/v2/chat`, so a local
 * `REACT_APP_SPARK_URL` gets the locally built bundle *and* the local API rather than
 * mixing a deployed bundle with a local backend.
 */
export const SparkChatWidget = ({
  charger,
  label,
  dateTimeRange,
  trigger,
  disabled,
  containerId = DEFAULT_SPARK_CHAT_CONTAINER_ID,
  buttonId = DEFAULT_SPARK_CHAT_BUTTON_ID,
}: SparkChatWidgetProps) => {
  const widgetInitializedRef = useRef(false);
  const chargerRef = useRef(charger);
  const dateTimeRangeRef = useRef(dateTimeRange);
  const triggerRef = useRef(trigger);
  const deployedSparkUrl = useSelector(getSparkUrl);
  const setSparkChatDisabled = useSetSparkChatDisabled(buttonId);

  const sparkUrl = useMemo(
    () => (process.env.REACT_APP_SPARK_URL || deployedSparkUrl).replace(/\/$/, ''),
    [deployedSparkUrl],
  );

  // `getParams` is called synchronously by the bundle at click time, so it has to read
  // the latest values from refs rather than close over this render's props.
  useEffect(() => {
    chargerRef.current = charger;
    dateTimeRangeRef.current = dateTimeRange;
    triggerRef.current = trigger;
  }, [charger, dateTimeRange, trigger]);

  const getAuthToken = useCallback(async () => {
    try {
      const session = await Auth.currentSession();
      return session.getAccessToken().getJwtToken();
    } catch {
      // The widget treats an empty token as "no auth"; the proxy will answer 401 and
      // the bundle retries once through this same callback.
      return '';
    }
  }, []);

  const initializeWidget = useCallback(() => {
    if (!window.SparkWidget || widgetInitializedRef.current || !sparkUrl) {
      return;
    }
    const chatContainer = document.getElementById(containerId);
    if (chatContainer) {
      chatContainer.innerHTML = '';
    }
    window.SparkWidget.init({
      containerId,
      buttonId,
      buttonText: label,
      buttonClass: 'spark-chat-button',
      buttonHint: 'Analyze charger data with Spark assistant',
      getAuthToken,
      getParams: () => {
        const currentCharger = chargerRef.current;
        const [startDate, endDate] = dateTimeRangeRef.current.selDates;
        const hours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
        return {
          chargerId: currentCharger.id || null,
          chargerName: currentCharger.name || null,
          // The charger's own zone: the window the user picked is expressed in it, and
          // Spark AI reads `launch_context.time_window` as local to this value. The
          // browser's zone would silently shift the window it analyzes.
          timezone:
            currentCharger.location?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          endTime: endDate,
          delta: hours || 24,
          companyId: currentCharger.ownerId || '',
          serverUrl: sparkUrl,
          apiEndpoint: SPARK_CHAT_ENDPOINT,
          botAvatarSrc: `${sparkUrl}${AVATAR_BASE}/logo-spark-chat.png`,
          userAvatarSrc: `${sparkUrl}${AVATAR_BASE}/user-icon.png`,
          trigger: triggerRef.current,
        };
      },
    });
    widgetInitializedRef.current = true;
  }, [containerId, buttonId, label, getAuthToken, sparkUrl]);

  useEffect(() => {
    if (!sparkUrl) {
      return;
    }
    loadSparkScriptOnce(`${sparkUrl}${BUNDLE_PATH}`, initializeWidget);
  }, [sparkUrl, initializeWidget]);

  useEffect(() => {
    setSparkChatDisabled(!!disabled);
  }, [disabled, setSparkChatDisabled]);

  return (
    <>
      {/* Container for this Spark chat widget instance */}
      <div id={containerId} />
      {/* The bundle renders its own button, so its styling has to travel with it. */}
      <style>
        {`
        .spark-chat-button {
          background-color: rgb(17, 125, 184);
          color: white;
          border: none;
          border-radius: 4px;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: background-color 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          height: 40px;
          justify-content: center;
        }

        .spark-chat-button:hover {
          background-color: rgb(6, 92, 145);
        }

        .spark-chat-button:disabled {
          background: #cccccc;
          cursor: not-allowed;
          box-shadow: none;
        }
      `}
      </style>
    </>
  );
};
